import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import { format, subDays, isWithinInterval, parseISO } from 'date-fns';
import { FiTrendingUp, FiTrendingDown, FiPackage, FiDollarSign, FiAlertTriangle, FiShoppingCart, FiCalendar, FiFilter, FiDownload, FiBarChart2, FiPieChart, FiActivity } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const SalesReport = () => {
  const { inventoryHistory, products } = useInventory();
  const [dateRange, setDateRange] = useState('30'); // days
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('summary'); // summary, detailed, charts

  const filteredHistory = inventoryHistory.filter(entry => {
    const entryDate = parseISO(entry.timestamp);
    const cutoffDate = subDays(new Date(), parseInt(dateRange));
    
    // Filter by date range
    const inDateRange = entryDate >= cutoffDate;
    
    // Filter by type
    const typeMatch = filterType === 'all' || entry.type === filterType;
    
    return inDateRange && typeMatch;
  });

  // Calculate statistics
  const stats = {
    sold: filteredHistory.filter(entry => entry.type === 'sale').reduce((sum, entry) => sum + Math.abs(entry.quantity), 0),
    damaged: filteredHistory.filter(entry => entry.type === 'damaged').reduce((sum, entry) => sum + Math.abs(entry.quantity), 0),
    internalUse: filteredHistory.filter(entry => entry.type === 'internal_use').reduce((sum, entry) => sum + Math.abs(entry.quantity), 0),
    purchased: filteredHistory.filter(entry => entry.type === 'purchase').reduce((sum, entry) => sum + entry.quantity, 0)
  };

  // Calculate financial impact
  const financialStats = {
    sold: filteredHistory.filter(entry => entry.type === 'sale').reduce((sum, entry) => {
      const product = products.find(p => p.id === entry.productId);
      return sum + (Math.abs(entry.quantity) * (product?.localPrice || 0));
    }, 0),
    damaged: filteredHistory.filter(entry => entry.type === 'damaged').reduce((sum, entry) => {
      const product = products.find(p => p.id === entry.productId);
      return sum + (Math.abs(entry.quantity) * (product?.localPrice || 0));
    }, 0),
    internalUse: filteredHistory.filter(entry => entry.type === 'internal_use').reduce((sum, entry) => {
      const product = products.find(p => p.id === entry.productId);
      return sum + (Math.abs(entry.quantity) * (product?.localPrice || 0));
    }, 0)
  };

  const exportReport = () => {
    const csvContent = [
      ['Date', 'Product', 'Type', 'Quantity', 'Reason', 'User', 'Value Lost/Gained'],
      ...filteredHistory.map(entry => {
        const product = products.find(p => p.id === entry.productId);
        const value = entry.type === 'purchase' 
          ? entry.quantity * (product?.localPrice || 0)
          : -Math.abs(entry.quantity) * (product?.localPrice || 0);
          
        return [
          format(new Date(entry.timestamp), 'yyyy-MM-dd HH:mm:ss'),
          product ? product.name : 'Unknown Product',
          entry.type,
          entry.quantity,
          entry.reason,
          entry.user,
          `$${value.toFixed(2)}`
        ];
      })
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const StatCard = ({ title, value, change, icon, color, trend }) => {
    const colorClasses = {
      green: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
      red: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
      blue: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
      yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800',
      purple: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800'
    };

    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{title}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
            {trend && (
              <div className={`flex items-center mt-2 text-sm ${trend > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                <SafeIcon icon={trend > 0 ? FiTrendingUp : FiTrendingDown} className="h-4 w-4 mr-1" />
                {Math.abs(trend)}%
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg border ${colorClasses[color]}`}>
            <SafeIcon icon={icon} className="h-6 w-6" />
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Sales Report</h1>
          <p className="text-gray-600 dark:text-gray-400">Track sales, damages, and inventory movements</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button 
            onClick={exportReport}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
          >
            <SafeIcon icon={FiDownload} className="h-4 w-4 mr-2" />
            Export CSV
          </button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.1 }}
        className="flex flex-col sm:flex-row gap-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiCalendar} className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          <select 
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiFilter} className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          <select 
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Activities</option>
            <option value="sale">Sales</option>
            <option value="damaged">Damaged</option>
            <option value="internal_use">Internal Use</option>
            <option value="purchase">Purchases</option>
          </select>
        </div>
      </motion.div>

      {/* Statistics Grid */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <StatCard title="Items Sold" value={stats.sold} icon={FiShoppingCart} color="blue" trend={8} />
        <StatCard title="Items Damaged" value={stats.damaged} icon={FiAlertTriangle} color="red" trend={-5} />
        <StatCard title="Internal Use" value={stats.internalUse} icon={FiPackage} color="yellow" trend={2} />
        <StatCard title="Revenue from Sales" value={`$${financialStats.sold.toFixed(2)}`} icon={FiDollarSign} color="green" trend={12} />
      </motion.div>

      {/* Financial Impact */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Financial Impact Analysis</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-sm text-green-600 dark:text-green-400 mb-1">Revenue Generated</p>
            <p className="text-2xl font-bold text-green-900 dark:text-green-100">${financialStats.sold.toFixed(2)}</p>
          </div>
          <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400 mb-1">Value Lost (Damaged)</p>
            <p className="text-2xl font-bold text-red-900 dark:text-red-100">${financialStats.damaged.toFixed(2)}</p>
          </div>
          <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-1">Value Used Internally</p>
            <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">${financialStats.internalUse.toFixed(2)}</p>
          </div>
        </div>
      </motion.div>

      {/* Detailed Activity List */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.4 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
      >
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Activity Details</h2>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto custom-scrollbar">
          {filteredHistory.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              No activity found for the selected period
            </div>
          ) : (
            filteredHistory.map((entry, index) => {
              const product = products.find(p => p.id === entry.productId);
              
              const getIcon = (type) => {
                switch (type) {
                  case 'sale': return FiShoppingCart;
                  case 'damaged': return FiAlertTriangle;
                  case 'internal_use': return FiPackage;
                  case 'purchase': return FiTrendingUp;
                  default: return FiActivity;
                }
              };

              const getColorClass = (type) => {
                switch (type) {
                  case 'sale': return 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/30 dark:border-blue-800';
                  case 'damaged': return 'text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/30 dark:border-red-800';
                  case 'internal_use': return 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-900/30 dark:border-yellow-800';
                  case 'purchase': return 'text-green-600 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-900/30 dark:border-green-800';
                  default: return 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-400 dark:bg-gray-900/30 dark:border-gray-700';
                }
              };

              const icon = getIcon(entry.type);
              const colorClass = getColorClass(entry.type);

              return (
                <motion.div 
                  key={entry.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.02 }}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-full border ${colorClass}`}>
                      <SafeIcon icon={icon} className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {product ? product.name : 'Unknown Product'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {format(new Date(entry.timestamp), 'MMM dd, yyyy HH:mm')}
                        </p>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-gray-600 dark:text-gray-400">{entry.reason}</p>
                        <span className={`text-sm font-medium ${entry.quantity > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {entry.quantity > 0 ? '+' : ''}{entry.quantity}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-gray-500 dark:text-gray-500">by {entry.user}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          Value: ${Math.abs(entry.quantity * (product?.localPrice || 0)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default SalesReport;